import React, { useState, useEffect, useMemo, createContext, useContext, ReactNode, useCallback } from 'react';
import { authService } from 'services/auth/auth_service';

export enum ModuleEnum {
  USERS = 'USERS',
  DOCUMENTS = 'DOCUMENTS',
  GOVERNANCE = 'GOVERNANCE',
  CIFA = 'CIFA',
  INSTITUTIONS = 'INSTITUTIONS',
  ADHESION_TERM = 'ADHESION_TERM',
  PROFILES = 'PROFILES',
  FORMS = 'FORMS',
}

export enum PermissionType {
  CREATE = 'create',
  READ = 'read',
  LIST = 'list',
  UPDATE = 'update',
  DELETE = 'delete',
  IS_ACTIVE = 'is_active',
}

export interface IGranularPermission {
  module: {
    uuid: string;
    name: string;
    description: string;
  };
  permissions: Record<PermissionType, boolean>;
}

export interface IPermissionUserContext {
  uuid: string;
  institutionUuid?: string;
  permissionUuid: string;
  roleList: string[];
  granularPermissions: IGranularPermission[];
}

interface PermissionsContextData {
  user: IPermissionUserContext | null;
  hasPermission: (
    module: ModuleEnum,
    permission: PermissionType
  ) => boolean;
  getModulePermissions: (module: ModuleEnum) => Partial<Record<PermissionType, boolean>>;
}

const PermissionsContext = createContext<PermissionsContextData>({} as PermissionsContextData);

function getUserPermissionFromStorage(): IPermissionUserContext | null {
  try {
    const userPermissions = authService.getPermissionsLocalStorage();
    if (!userPermissions) return null;
    return userPermissions;
  } catch {
    return null;
  }
}

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const handler = () => setVersion(v => v + 1);
    window.addEventListener('permissionsChanged', handler);
    return () => window.removeEventListener('permissionsChanged', handler);
  }, []);

  const user = useMemo(() => getUserPermissionFromStorage(), [version]);
  const granularPermissions = user?.granularPermissions ?? [];

  const permissionsMap = useMemo(() => {
    const map: Record<ModuleEnum, Partial<Record<PermissionType, boolean>>> = {} as any;
    granularPermissions.forEach((gp: IGranularPermission) => {
      const moduleName = gp.module.name as ModuleEnum;
      map[moduleName] = gp.permissions;
    });
    return map;
  }, [granularPermissions]);

  const ADMIN_ROLES = ['ADMINISTRADOR'];

  const hasPermission = useCallback(
  (module: ModuleEnum, permission: PermissionType) => {
    if (user?.roleList?.some(role => ADMIN_ROLES.includes(role))) {
      return true;
    }
    return !!permissionsMap[module]?.[permission];
  },
  [user, permissionsMap]
);

const getModulePermissions = useCallback(
  (module: ModuleEnum) => {
    return permissionsMap[module] || {};
  },
  [permissionsMap]
);


  const permissionsContextValue = useMemo(() => ({
  user,
  hasPermission,
  getModulePermissions,
}), [user, hasPermission, getModulePermissions]);


  return (
  <PermissionsContext.Provider value={permissionsContextValue}>
    {children}
  </PermissionsContext.Provider>
);
};

export const usePermissions = () => useContext(PermissionsContext);
